<template>
    <div class="workspace-controls">
        <Teleport defer to="#chart-bottom-actions">
        <div class="workspace-action-bar" aria-label="Analysis actions">
            <button @click="act('strategies')" :class="{active:model.strategyDockOpen}" :aria-expanded="model.strategyDockOpen" aria-label="Strategies" title="Strategies"><WorkspaceIcon name="trade" /></button>
            <button @click="act('buildStrategy')" aria-label="Strategy Builder" title="Strategy Builder"><WorkspaceIcon name="objects" /></button>
            <i></i
            ><button @click="act('layout')" :aria-label="model.layout === 1 ? 'Single chart' : model.layout + ' charts'" title="Chart layout">
                <WorkspaceIcon name="layout" />
            </button>
            <button @click="act('objects')" aria-label="Object tree" title="Object tree">
                <WorkspaceIcon name="objects" />
            </button>
            <i></i
            ><button
                :disabled="!model.canUndo"
                @click="act('undo')"
                aria-label="Undo drawings"
            >
                <WorkspaceIcon name="undo" /></button
            ><button
                :disabled="!model.canRedo"
                @click="act('redo')"
                aria-label="Redo drawings"
            >
                <WorkspaceIcon name="redo" />
            </button>
            <div class="workspace-spacer"></div>

            <button @click="act('bots')" aria-label="Bot trading" title="Bot trading">
                <WorkspaceIcon name="bot" />
            </button>
        </div>
        </Teleport>
        <Teleport defer to="#chart-top-replay">
            <button
                @click="act('replay')" aria-label="Bar replay" title="Bar replay"
                :class="{ active: model.replay.active }"
                :disabled="model.barCount < 2"
            >
                <WorkspaceIcon name="replay" />
            </button>
        </Teleport>
        <Teleport defer to="#chart-bottom-vt-actions">
        <div class="workspace-vt-action-bar" aria-label="Visual Trading actions">
            <button
                @click="act('toggleVT')"
                :class="{ active: model.vtActive }"
                :aria-pressed="model.vtActive"
                title="Toggle Visual Trading indicators"
                aria-label="Toggle Visual Trading indicators"
            >
                <WorkspaceIcon name="layers" />
            </button>
            <button
                @click="act('vtManager')"
                :class="{ active: model.panel === 'vtManager' }"
                title="Manage Visual Trading indicator options"
                aria-label="Manage Visual Trading indicator options"
            >
                <WorkspaceIcon name="settings" />
            </button>
        </div>
        </Teleport>
        <div
            v-if="model.replay.active"
            class="workspace-replay-bar"
            aria-label="Bar replay controls"
        >
            <span class="workspace-replay-label"
                ><WorkspaceIcon name="replay" />REPLAY</span
            >
            <button
                @click="act('replayPlay')"
                :aria-label="
                    model.replay.playing ? 'Pause replay' : 'Play replay'
                "
            >
                <WorkspaceIcon
                    :name="model.replay.playing ? 'pause' : 'play'"
                />
            </button>
            <button
                @click="act('replayStep')"
                aria-label="Advance one bar"
                :disabled="model.replay.index >= model.replay.total"
            >
                <WorkspaceIcon name="step" />
            </button>
            <select
                :value="model.replay.speed"
                @change="act('replaySpeed', Number($event.target.value))"
                aria-label="Replay speed"
            >
                <option
                    v-for="speed in [0.5, 1, 2, 3, 5, 10]"
                    :value="speed"
                    :key="speed"
                >
                    {{ speed }}×
                </option>
            </select>
            <input
                type="range"
                min="1"
                :max="model.replay.total"
                :value="model.replay.index"
                @input="act('replaySeek', Number($event.target.value))"
                aria-label="Replay position"
            />
            <span>{{ model.replay.index }} / {{ model.replay.total }}</span
            ><time>{{ model.replay.time }}</time>
            <button @click="act('replayExit')">Exit replay</button>
        </div>
        <Teleport defer to="#chart-sidebar-content" :disabled="!isSidebarPanel">
        <div
            v-if="model.panel"
            class="workspace-modal-backdrop" :class="{'workspace-sidebar-backdrop':isSidebarPanel}"
            @click.self="act('close')"
        >
            <section
                class="workspace-dialog"
                :class="'workspace-dialog-' + model.panel"
                role="dialog"
                :aria-modal="isSidebarPanel ? undefined : true"
                :aria-label="titles[model.panel]"
                @keydown.esc="act('close')"
            >
                <header>
                    <div>
                        <span class="workspace-eyebrow">TRADINGCANDLE</span>
                        <h2>{{ titles[model.panel] }}</h2>
                    </div>
                    <button
                        class="workspace-icon-button"
                        @click="act('close')"
                        aria-label="Close dialog"
                    >
                        <WorkspaceIcon name="close" />
                    </button>
                </header>
                <div class="workspace-dialog-content">
                    <template v-if="model.panel === 'layout'">
                        <p class="workspace-help">
                            One workspace. Multiple perspectives. Each pane has
                            its own timeframe.
                        </p>
                        <div class="workspace-layout-options">
                            <button
                                v-for="count in [1, 2, 4]"
                                :key="count"
                                :class="{ active: model.layout === count }"
                                @click="act('setLayout', count)"
                            >
                                <span
                                    :class="[
                                        'workspace-layout-preview',
                                        'layout-' + count,
                                    ]"
                                    ><i v-for="n in count" :key="n"></i></span
                                >{{
                                    count === 1
                                        ? 'Single'
                                        : count === 2
                                        ? 'Side by side'
                                        : 'Four charts'
                                }}
                            </button>
                        </div>
                        <label class="workspace-checkbox"
                            ><input
                                type="checkbox"
                                :checked="model.syncRange"
                                @change="
                                    act('syncRange', $event.target.checked)
                                "
                            />Synchronize visible time range</label
                        >
                    </template>

                    <MarketAnalysis v-if="model.panel === 'orderflow'" :key="model.panel" :panel="model.panel" :symbol="model.symbol" :capabilities="model.capabilities" :candles="model.candles" />
                    <template v-if="model.panel === 'commands'">
                        <input
                            ref="commandInput"
                            class="workspace-search-input"
                            v-model="commandQuery"
                            placeholder="Search actions, tools and panels…"
                            aria-label="Search actions"
                            autofocus
                        />
                        <div class="workspace-command-list">
                            <button
                                v-for="command in filteredCommands"
                                :key="command.action"
                                @click="act(command.action)"
                            >
                                <WorkspaceIcon :name="command.icon" /><span>{{
                                    command.label
                                }}</span
                                ><kbd v-if="command.shortcut">{{
                                    command.shortcut
                                }}</kbd>
                            </button>
                            <p
                                v-if="!filteredCommands.length"
                                class="workspace-empty"
                            >
                                No matching commands.
                            </p>
                        </div>
                    </template>
                    <template v-if="model.panel === 'objects'">
                        <input class="sidebar-search" v-model="objectSearch" placeholder="Search drawings and indicators" aria-label="Search chart objects" />
                        <div class="workspace-section-heading">
                            <span>{{ model.objects.length }} chart objects</span
                            ><button @click="act('toggleAllObjects')">
                                Show / hide all
                            </button>
                        </div>
                        <div
                            v-for="object in filteredObjects"
                            :key="object.key"
                            class="workspace-object-row"
                        >
                            <WorkspaceIcon
                                :name="object.drawing ? 'objects' : 'compare'"
                            />
                            <div>
                                <strong>{{ object.name }}</strong
                                ><small
                                    >{{
                                        object.drawing ? 'Drawing' : 'Indicator'
                                    }}
                                    · {{ object.side }}</small
                                >
                            </div>
                            <button
                                @click="act('toggleObject', object.key)"
                                :aria-label="
                                    (object.visible ? 'Hide ' : 'Show ') +
                                    object.name
                                "
                                :class="{ muted: !object.visible }"
                            >
                                <WorkspaceIcon name="eye" /></button
                            ><button
                                v-if="object.drawing"
                                @click="act('lockObject', object.key)"
                                :aria-label="
                                    (object.locked ? 'Unlock ' : 'Lock ') +
                                    object.name
                                "
                                :class="{ active: object.locked }"
                            >
                                <WorkspaceIcon name="lock" /></button
                            ><button
                                @click="act('deleteObject', object.key)"
                                :aria-label="'Remove ' + object.name"
                            >
                                <WorkspaceIcon name="delete" />
                            </button>
                        </div>
                        <p v-if="!model.objects.length" class="workspace-empty">
                            Draw on the chart or add an indicator to manage it
                            here.
                        </p>
                    </template>
                    <template v-if="model.panel === 'vtManager'">
                        <p class="workspace-help">
                            Choose which Visual Trading indicators appear on
                            your charts. Your choices are saved and applied
                            every time you turn Visual Trading on.
                        </p>
                        <div
                            v-for="indicator in model.vtIndicators"
                            :key="indicator.index"
                            class="workspace-object-row"
                        >
                            <WorkspaceIcon name="layers" />
                            <div>
                                <strong>{{ indicator.name }}</strong>
                            </div>
                            <button
                                @click="act('toggleVTIndicator', indicator.index)"
                                :aria-label="
                                    (indicator.visible ? 'Hide ' : 'Show ') +
                                    indicator.name
                                "
                                :class="{ muted: !indicator.visible }"
                            >
                                <WorkspaceIcon name="eye" />
                            </button>
                        </div>
                        <p
                            v-if="!model.vtIndicators.length"
                            class="workspace-empty"
                        >
                            No Visual Trading indicators are configured.
                        </p>
                    </template>
                    <template v-if="model.panel === 'alerts'">
                        <div class="sidebar-tabs"><button :class="{active:alertTab==='alerts'}" @click="alertTab='alerts'">Alerts</button><button :class="{active:alertTab==='log'}" @click="alertTab='log'">Log</button></div>
                        <div class="sidebar-tools"><button @click="alertComposer=true;alertTab='alerts'" aria-label="New alert" title="New alert">+</button><input v-model="alertSearch" type="search" placeholder="Search alerts" aria-label="Search alerts" /><button @click="alertNewest=!alertNewest" aria-label="Sort alerts" title="Sort alerts">↕</button></div>
                        <div v-if="alertTab==='log'" class="sidebar-alert-log"><article v-for="event in model.alertLog || []" :key="event.key"><strong>{{ event.name }}</strong><p>{{ event.symbol }} · {{ format(event.price) }}</p><small>{{ new Date(event.time).toLocaleString() }}</small></article><p v-if="!model.alertLog?.length" class="workspace-empty">No alert triggers recorded on this device.</p></div>
                        <div v-if="alertTab==='alerts'">
                        <div v-if="!model.alerts.length && !alertComposer" class="sidebar-empty-state"><WorkspaceIcon name="alert" /><p>Alerts notify you when your conditions are met. Create one to get started.</p><button class="workspace-primary" @click="alertComposer=true">Create alert</button></div>
                        <div class="workspace-context-chip">
                            {{ model.symbol }}
                            <span>{{ model.timeframe }}</span>
                        </div>
                        <form
                            v-if="alertComposer || editingAlert"
                            @submit.prevent="submitAlertForm"
                            class="workspace-form"
                        >
                            <label
                                >Condition<select
                                    v-model="alertForm.condition"
                                    aria-label="Condition"
                                >
                                    <option value="crossing">Crossing</option>
                                    <option value="crossing_up">
                                        Crossing up
                                    </option>
                                    <option value="crossing_down">
                                        Crossing down
                                    </option>
                                    <option v-if="alertForm.drawing?.kind==='Parallel Channel'" value="inside">Inside channel</option><option v-if="alertForm.drawing?.kind==='Parallel Channel'" value="outside">Outside channel</option><option v-if="alertForm.drawing?.kind==='Parallel Channel'" value="entering">Entering channel</option><option v-if="alertForm.drawing?.kind==='Parallel Channel'" value="exiting">Exiting channel</option>
                                    <option value="above">Greater than</option>
                                    <option value="below">Less than</option>
                                </select></label
                            >
                            <p v-if="alertForm.drawing" class="workspace-form-wide">Linked to {{alertForm.drawing.kind}}. Anchor edits update this alert.</p>
                            <label v-if="alertForm.drawing?.kind?.startsWith('Fib')">Fibonacci level<input aria-label="Alert Fibonacci level" type="number" step="any" v-model.number="alertForm.drawing.level" /></label>
                            <label v-if="!alertForm.drawing"
                                >Price<input
                                    aria-label="Price"
                                    v-model.number="alertForm.target"
                                    type="number"
                                    step="any"
                                    min="0.00000001"
                                    required
                            /></label>
                            <label class="workspace-form-wide"
                                >Alert name<input
                                    v-model="alertForm.name"
                                    maxlength="100"
                                    placeholder="Price level to watch"
                            /></label>
                            <button
                                class="workspace-primary workspace-form-wide"
                                :disabled="
                                    !model.price ||
                                    (!editingAlert &&
                                        model.serverAlerts &&
                                        model.alertCapabilities.count >=
                                            model.alertCapabilities.maxAlerts)
                                "
                            >
                                {{ editingAlert ? 'Save alert' : 'Create alert' }}
                            </button>
                            <button
                                v-if="editingAlert"
                                type="button"
                                class="workspace-form-wide"
                                @click="cancelEditAlert"
                            >
                                Cancel edit
                            </button>
                        </form>
                        <p class="workspace-help" v-if="model.serverAlerts">
                            These alerts run on the server
                            ({{ model.alertCapabilities.count }}/{{
                                model.alertCapabilities.maxAlerts
                            }}
                            used) and notify by email/chat even if this tab is
                            closed. They fire once and can be rearmed below.
                        </p>
                        <p class="workspace-help" v-else>
                            Alerts evaluate observed prices while this chart is
                            open. They fire once and can be rearmed below.
                            Notifications stay in this workspace.
                        </p>
                        <div
                            v-for="alert in filteredAlerts"
                            :key="alert.source + ':' + alert.id"
                            class="workspace-alert-row"
                        >
                            <div>
                                <strong>{{ alert.name }}</strong
                                ><small
                                    >{{ alert.symbol }} ·
                                    {{ conditionLabels[alert.condition] }}
                                    {{ format(alert.target) }}
                                    <em v-if="alert.source === 'server'"
                                        >· account</em
                                    ></small
                                ><small
                                    v-if="alert.triggeredAt"
                                    class="positive"
                                    >Triggered at
                                    {{ format(alert.triggeredPrice) }}</small
                                >
                            </div>
                            <button
                                @click="startEditAlert(alert)"
                                aria-label="Edit alert"
                            >
                                <WorkspaceIcon name="settings" />
                            </button>
                            <button
                                @click="
                                    act('toggleAlert', {
                                        id: alert.id,
                                        source: alert.source,
                                    })
                                "
                                :class="{ active: alert.enabled }"
                            >
                                {{ alert.enabled ? 'Active' : 'Rearm' }}</button
                            ><button
                                @click="
                                    act('deleteAlert', {
                                        id: alert.id,
                                        source: alert.source,
                                    })
                                "
                                aria-label="Delete alert"
                            >
                                <WorkspaceIcon name="delete" />
                            </button>
                        </div>
                        </div>
                    </template>
                    <template v-if="model.panel === 'workspaces'">
                        <p class="workspace-help">
                            Save symbols, chart settings, drawings and indicators to your account. A local copy is kept if the account save fails.
                        </p>
                        <p v-if="model.cloudStatus" class="workspace-help" role="status">{{ model.cloudStatus }}</p>
                        <button :disabled="model.cloudBusy" @click="act('refreshWorkspaces')">Refresh account workspaces</button>
                        <form
                            @submit.prevent="
                                act('saveWorkspace', workspaceName)
                            "
                            class="workspace-inline-form"
                        >
                            <input
                                v-model="workspaceName"
                                placeholder="Workspace name"
                                maxlength="100"
                                required
                                aria-label="Workspace name"
                            /><button
                                class="workspace-primary"
                                :disabled="!model.barCount || model.cloudBusy"
                            >
                                Save workspace
                            </button>
                        </form>
                        <div
                            v-for="item in model.workspaces"
                            :key="item.id"
                            class="workspace-saved-row"
                        >
                            <div>
                                <strong>{{ item.name }}</strong
                                ><small
                                    >{{ item.symbol }} · {{ item.timeframe }} ·
                                    {{
                                        new Date(
                                            item.savedAt
                                        ).toLocaleDateString()
                                    }}</small
                                >
                            </div>
                            <button v-if="item.cloudPending && item.cloudRevision" @click="act('resolveWorkspaceConflict', item.id)">Keep local copy &amp; refresh</button>
                            <button @click="act('loadWorkspace', item.id)">
                                Open</button
                            ><button
                                @click="act('deleteWorkspace', item.id)"
                                :aria-label="'Delete ' + item.name"
                            >
                                <WorkspaceIcon name="delete" />
                            </button>
                        </div>
                        <div class="workspace-import-export">
                            <button
                                @click="act('exportWorkspace')"
                                :disabled="!model.barCount"
                            >
                                <WorkspaceIcon name="download" />Export JSON</button
                            ><label class="workspace-file-button"
                                >Import JSON<input
                                    type="file"
                                    accept=".json,application/json"
                                    @change="
                                        act(
                                            'importWorkspace',
                                            $event.target.files[0]
                                        );
                                        $event.target.value = '';
                                    "
                            /></label>
                        </div>
                    </template>
                    <template v-if="model.panel === 'compare'">
                        <p class="workspace-help">
                            Compare relative performance with a Binance spot or
                            perpetual symbol. The line is rebased to your
                            chart's first common bar, keeping percentage moves
                            comparable.
                        </p>
                        <form
                            @submit.prevent="
                                act('addComparison', compareSymbol)
                            "
                            class="workspace-inline-form"
                        >
                            <input
                                v-model="compareSymbol"
                                placeholder="ETHUSDT or BTCUSDT.P"
                                aria-label="Comparison symbol"
                                required
                            /><button
                                class="workspace-primary"
                                :disabled="model.comparing"
                            >
                                {{
                                    model.comparing
                                        ? 'Loading…'
                                        : 'Add comparison'
                                }}
                            </button>
                        </form>
                        <div
                            v-for="comparison in model.comparisons"
                            :key="comparison.symbol"
                            class="workspace-saved-row"
                        >
                            <span :style="{ color: comparison.color }">●</span>
                            <div>
                                <strong>{{ comparison.symbol }}</strong
                                ><small
                                    >{{ comparison.count }} aligned bars</small
                                >
                            </div>
                            <button
                                @click="
                                    act('removeComparison', comparison.symbol)
                                "
                                aria-label="Remove comparison"
                            >
                                <WorkspaceIcon name="delete" />
                            </button>
                        </div>
                    </template>
                    <template v-if="model.panel === 'date'"
                        ><p class="workspace-help">
                            Show a range from the currently loaded history.
                            Times are UTC.
                        </p>
                        <form
                            class="workspace-form"
                            @submit.prevent="act('dateRange', { ...dateForm })"
                        >
                            <label
                                >From (UTC)<input
                                    type="datetime-local"
                                    v-model="dateForm.start"
                                    required /></label
                            ><label
                                >To (UTC)<input
                                    type="datetime-local"
                                    v-model="dateForm.end"
                                    required /></label
                            ><button
                                class="workspace-primary workspace-form-wide"
                            >
                                Show range
                            </button>
                        </form></template
                    >
                    <template v-if="model.panel === 'settings'">
                        <div class="workspace-settings-grid">
                            <label
                                >Price precision<select
                                    :value="
                                        model.preferences.precision == null || (!model.preferences.precisionMode && model.preferences.precision === 2)
                                            ? 'auto'
                                            : model.preferences.precision
                                    "
                                    @change="
                                        act('preference', {
                                            key: 'precision',
                                            value: $event.target.value === 'auto' ? null : Number($event.target.value),
                                        })
                                    "
                                >
                                    <option value="auto">Auto (symbol)</option>
                                    <option
                                        v-for="n in [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 12, 16, 20]"
                                        :key="n"
                                        :value="n"
                                    >
                                        {{ n }} decimals
                                    </option>
                                </select></label
                            ><label
                                >Rising candles<input
                                    type="color"
                                    :value="model.preferences.up"
                                    @input="
                                        act('preference', {
                                            key: 'up',
                                            value: $event.target.value,
                                        })
                                    " /></label
                            ><label
                                >Falling candles<input
                                    type="color"
                                    :value="model.preferences.down"
                                    @input="
                                        act('preference', {
                                            key: 'down',
                                            value: $event.target.value,
                                        })
                                    " /></label
                            ><label class="workspace-checkbox"
                                ><input
                                    type="checkbox"
                                    :checked="model.preferences.grid"
                                    @change="
                                        act('preference', {
                                            key: 'grid',
                                            value: $event.target.checked,
                                        })
                                    "
                                />Grid lines</label
                            ><label class="workspace-checkbox"
                                ><input
                                    type="checkbox"
                                    :checked="model.preferences.volume"
                                    @change="
                                        act('preference', {
                                            key: 'volume',
                                            value: $event.target.checked,
                                        })
                                    "
                                />Volume</label
                            ><label class="workspace-checkbox"
                                ><input
                                    type="checkbox"
                                    :checked="model.preferences.priceLine"
                                    @change="
                                        act('preference', {
                                            key: 'priceLine',
                                            value: $event.target.checked,
                                        })
                                    "
                                />Last price line</label
                            ><label class="workspace-checkbox"
                                ><input
                                    type="checkbox"
                                    :checked="model.preferences.log"
                                    @change="
                                        act('preference', {
                                            key: 'log',
                                            value: $event.target.checked,
                                        })
                                    "
                                />Logarithmic price scale</label
                            >
                        </div>
                        <div class="workspace-section-heading">
                            <span>Appearance</span
                            ><button @click="act('theme')">
                                {{
                                    model.dark
                                        ? 'Switch to light'
                                        : 'Switch to dark'
                                }}
                            </button>
                        </div>
                        <p class="workspace-help">
                            Logarithmic scale requires positive prices. Chart
                            type and interval remain in the main toolbar.
                        </p>
                    </template>
                    <template v-if="model.panel === 'paper'">
                        <div class="sidebar-tabs paper-tabs"><button v-for="item in [{id:'trade',label:'Trade'},{id:'positions',label:'Positions'},{id:'orders',label:'Orders'},{id:'history',label:'History'}]" :key="item.id" :class="{active:paperTab===item.id}" @click="paperTab=item.id">{{ item.label }}</button></div>
                        <div class="workspace-paper-summary">
                            <div>
                                <small>Account equity</small
                                ><strong>{{ format(model.equity) }}</strong>
                            </div>
                            <div>
                                <small>Realized P&amp;L</small
                                ><strong
                                    :class="
                                        model.paper.realized >= 0
                                            ? 'positive'
                                            : 'negative'
                                    "
                                    >{{ format(model.paper.realized) }}</strong
                                >
                            </div>
                            <span class="workspace-simulated">SIMULATED</span>
                        </div>
                        <div v-if="paperTab==='trade'">
                        <p class="workspace-help">
                            Practice in quote-currency units with 100,000
                            starting balance and no leverage. Orders use
                            observed closing prices, without fees, slippage or
                            real broker execution. Orders evaluate while this
                            chart is open. Paper orders are paused during
                            replay.
                        </p>
                        <form
                            class="workspace-form"
                            @submit.prevent="
                                act('paperOrder', { ...orderForm })
                            "
                        >
                            <label
                                >Side<select
                                    aria-label="Side"
                                    v-model="orderForm.side"
                                >
                                    <option value="buy">Buy / cover</option>
                                    <option value="sell">Sell / short</option>
                                </select></label
                            ><label
                                >Order type<select
                                    aria-label="Order type"
                                    v-model="orderForm.type"
                                >
                                    <option value="market">Market</option>
                                    <option value="limit">Limit</option>
                                    <option value="stop">Stop</option>
                                </select></label
                            ><label
                                >Quantity<input
                                    aria-label="Quantity"
                                    v-model.number="orderForm.quantity"
                                    type="number"
                                    min="0.00000001"
                                    step="any"
                                    required /></label
                            ><label v-if="orderForm.type !== 'market'"
                                >Order price<input
                                    v-model.number="orderForm.price"
                                    type="number"
                                    min="0.00000001"
                                    step="any"
                                    required /></label
                            ><button
                                class="workspace-primary workspace-form-wide"
                                :disabled="!model.price || model.replay.active"
                            >
                                Place simulated order · {{ model.symbol }}
                            </button>
                        </form>
                        </div>
                        <div v-if="paperTab==='positions'">
                        <h3>Positions</h3>
                        <div
                            v-for="(position, key) in model.paper.positions"
                            :key="key"
                            class="workspace-saved-row"
                        >
                            <div>
                                <strong>{{ position.symbol }}</strong
                                ><small
                                    >{{ position.quantity }} @
                                    {{ format(position.average) }} · P&amp;L
                                    {{
                                        format(
                                            (position.mark - position.average) *
                                                position.quantity
                                        )
                                    }}</small
                                >
                            </div>
                            <button
                                @click="act('closePaperPosition', key)"
                                :disabled="
                                    key !== model.key || model.replay.active
                                "
                            >
                                Close
                            </button>
                        </div>
                        <p
                            v-if="!Object.keys(model.paper.positions).length"
                            class="workspace-empty"
                        >
                            No open positions.
                        </p>
                        </div>
                        <div v-if="paperTab==='orders'">
                        <h3>Pending orders</h3>
                        <div
                            v-for="order in model.paper.orders"
                            :key="order.id"
                            class="workspace-saved-row"
                        >
                            <div>
                                <strong
                                    >{{ order.side }} {{ order.quantity }}
                                    {{ order.symbol }}</strong
                                ><small
                                    >{{ order.type }} @
                                    {{ format(order.price) }}</small
                                >
                            </div>
                            <button @click="act('cancelPaperOrder', order.id)">
                                Cancel
                            </button>
                        </div>
                        </div>
                        <div v-if="paperTab==='history'">
                        <h3>Recent fills</h3>
                        <div
                            v-for="fill in model.paper.fills.slice(0, 10)"
                            :key="fill.id"
                            class="workspace-saved-row"
                        >
                            <div>
                                <strong
                                    >{{ fill.side }} {{ fill.quantity }}
                                    {{ fill.symbol }}</strong
                                ><small>{{
                                    fill.rejected ||
                                    'Filled @ ' + format(fill.price)
                                }}</small>
                            </div>
                            <small>{{
                                new Date(fill.time).toLocaleTimeString()
                            }}</small>
                        </div>
                        </div>
                    </template>
                </div>
            </section>
        </div>
        </Teleport>
        <div v-if="model.toast" class="workspace-toast" role="status">
            <WorkspaceIcon :name="model.toast.icon || 'save'" /><span>{{
                model.toast.message
            }}</span
            ><button
                @click="act('dismissToast')"
                aria-label="Dismiss notification"
            >
                ×
            </button>
        </div>
    </div>
</template>
<script>
import MarketAnalysis from './MarketAnalysis.vue';
import AutomationJourney from '../components/AutomationJourney.vue';
import WorkspaceIcon from './WorkspaceIcon.vue';
export default {
    components: { AutomationJourney, WorkspaceIcon, MarketAnalysis },
    props: { model: Object },
    data() {
        return {
            commandQuery: '',
            objectSearch:'', paperTab:'trade',
            workspaceName: '',
            compareSymbol: '',
            dateForm: { start: '', end: '' },
            alertForm: { name: '', target: null, condition: 'crossing' },
            editingAlert: null,
            alertTab:'alerts', alertComposer:false, alertSearch:'', alertNewest:true,
            orderForm: {
                side: 'buy',
                type: 'market',
                quantity: 1,
                price: null,
            },
            titles: {
                layout: 'Chart layout',
                commands: 'Quick search',
                objects: 'Object tree',
                alerts: 'Alerts',
                workspaces: 'Your workspaces',
                compare: 'Compare symbols',
                settings: 'Chart settings',
                vtManager: 'Visual Trading Indicators',
                paper: 'Paper trading',
                orderflow: 'Order Book',
                backtest: 'Strategy backtest',
                date: 'Go to date range',
            },
            conditionLabels: {
                crossing: 'crosses',
                crossing_up: 'crosses above',
                crossing_down: 'crosses below',
                above: 'above',
                below: 'below',
            },
        };
    },
    computed: {
        filteredAlerts() { return this.model.alerts.filter(alert=>(alert.name+' '+alert.symbol).toLowerCase().includes(this.alertSearch.toLowerCase())).slice().sort((a,b)=>this.alertNewest?Number(b.createdAt||b.triggeredAt||0)-Number(a.createdAt||a.triggeredAt||0):String(a.name).localeCompare(String(b.name))) },

        filteredObjects() { return this.model.objects.filter(object=>object.name.toLowerCase().includes(this.objectSearch.toLowerCase())) },
        isSidebarPanel() { return ['alerts','orderflow','objects','paper'].includes(this.model.panel) },
        filteredCommands() {
            const query = this.commandQuery.toLowerCase();
            return this.model.commands.filter((c) =>
                c.label.toLowerCase().includes(query)
            );
        },
    },
    watch: {
        'model.panel'(panel) {
            if (panel === 'commands') {
                this.commandQuery = '';
                this.$nextTick(() => this.$refs.commandInput?.focus());
            }
            if (panel === 'alerts') {
                this.alertComposer=false; this.alertTab='alerts';
                this.editingAlert = null;
                this.alertForm = {
                    name: '',
                    target: this.model.price,
                    condition: 'crossing',
                };
            }
            if (panel === 'workspaces')
                this.workspaceName = this.model.name || '';
            if (panel === 'paper') this.orderForm.price = this.model.price;
        },
    },
    methods: {
        act(action, payload) {
            this.$emit('action', { action, payload });
        },
        format(value) {
            return Number(value || 0).toLocaleString(undefined, {
                maximumFractionDigits: 6,
            });
        },
        submitAlertForm() {
            if (this.editingAlert) {
                this.act('editAlert', {
                    ...this.editingAlert,
                    ...this.alertForm,
                });
                this.editingAlert = null;
            } else {
                this.act('createAlert', { ...this.alertForm });
            }
            this.alertForm = {
                name: '',
                target: this.model.price,
                condition: 'crossing',
            };
        },
        startEditAlert(alert) {
            this.alertComposer=true; this.alertTab='alerts';
            this.editingAlert = { id: alert.id, source: alert.source };
            this.alertForm = {
                name: alert.name,
                target: alert.target,
                condition: alert.condition,
                drawing: alert.drawing?JSON.parse(JSON.stringify(alert.drawing)):null,
            };
        },
        cancelEditAlert() {
            this.editingAlert = null;
            this.alertForm = {
                name: '',
                target: this.model.price,
                condition: 'crossing',
            };
        },
        startAddAlert(price,drawing=null) {
            this.alertComposer=true; this.alertTab='alerts';
            this.editingAlert = null;
            this.alertForm = {
                name: '',
                target: Number.isFinite(price) ? price : this.model.price,
                drawing,
                condition: 'crossing',
            };
        },
    },
};
</script>
